import { useEffect, useState } from "react";
import { api, formatApiError, formatTZS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Upload, X, Clock } from "lucide-react";

const EMPTY = { title: "", description: "", image: "", price: 0, duration: "", order: 0, is_active: true, includes: [] };

export default function ActivitiesPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/activities"); setRows(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: rows.length + 1 }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("folder", "activities");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}` }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = { ...form, price: Number(form.price), order: Number(form.order), includes: (form.includes || []).filter(Boolean) };
      if (editing) await api.put(`/admin/activities/${editing.id}`, payload);
      else await api.post("/admin/activities", payload);
      toast.success(editing ? "Activity updated" : "Activity added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => { if (!confirm("Delete this activity?")) return; try { await api.delete(`/admin/activities/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-6" data-testid="activities-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Activities &amp; Tours</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Curated experiences guests can book — safari trips, coffee tours, cultural visits. Appear on the landing page.</p>
        </div>
        <Button onClick={openNew} data-testid="new-activity-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]"><Plus className="w-4 h-4 mr-1" /> Add activity</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rows.map((a) => (
          <Card key={a.id} className={`overflow-hidden hover-lift ${a.is_active ? "" : "opacity-60"}`} data-testid={`activity-card-${a.id}`}>
            <div className="h-40 bg-[color:var(--sand)] relative">
              {a.image && <img src={a.image} alt={a.title} className="w-full h-full object-cover" />}
              {!a.is_active && <Badge className="absolute top-3 right-3 bg-slate-500 text-white">Hidden</Badge>}
            </div>
            <CardContent className="p-5">
              <p className="font-heading text-lg text-[color:var(--deep)]">{a.title}</p>
              <p className="text-xs text-[color:var(--muted-text)] mt-1 line-clamp-2">{a.description}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-[color:var(--brand)]">{a.price > 0 ? formatTZS(a.price) : "Free"}</span>
                {a.duration && <span className="text-xs text-[color:var(--muted-text)] flex items-center gap-1"><Clock className="w-3 h-3" /> {a.duration}</span>}
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`edit-activity-${a.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="destructive" onClick={() => remove(a.id)} data-testid={`del-activity-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="activity-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit activity" : "Add activity"}</DialogTitle><DialogDescription>Shown in the &quot;Activities &amp; Tours&quot; section of the landing page.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Serengeti Safari" data-testid="act-title" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="act-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price (TZS, 0 = Free)</Label><Input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="act-price" /></div>
              <div><Label>Duration</Label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g. Half day" data-testid="act-duration" /></div>
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="act-order" /></div>
              <div className="flex items-end gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="act-active" /><Label>Active (visible to guests)</Label></div>
            </div>
            <div>
              <Label>What's included</Label>
              <Input
                value={(form.includes || []).join(", ")}
                onChange={(e) => setForm({ ...form, includes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                placeholder="Meals, Guide, Transport, Park fees"
                className="mt-1"
                data-testid="act-includes"
              />
              <p className="text-xs text-[color:var(--muted-text)] mt-1">Comma-separated tags — shown as chips on the landing page and booking form.</p>
              {form.includes && form.includes.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {form.includes.map((t, i) => (
                    <span key={i} className="text-xs font-ui text-[color:var(--brand)] bg-[color:var(--brand)]/10 px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Photo</Label>
              <div className="mt-2 flex items-center gap-3">
                {form.image ? (
                  <div className="relative w-32 h-24 rounded-lg overflow-hidden">
                    <img src={form.image} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setForm({ ...form, image: "" })} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ) : null}
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[color:var(--brand)]/30 hover:bg-[color:var(--sand)] text-sm">
                  <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : (form.image ? "Replace" : "Upload")}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="act-upload" />
                </label>
              </div>
              <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Or paste image URL" className="mt-2" data-testid="act-image-url" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-activity-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">{editing ? "Save" : "Add activity"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
